import type { ComponentConfig } from "@puckeditor/core"
import { SectionBase, sectionStyle } from "../lib/section"
import { getResolvers, SiteProduct } from "../lib/resolvers"
import { ProductTile } from "../lib/product-tile"

export type ProductCardProps = SectionBase & {
  productHandle: string
  /** "horizontal" | "vertical" */
  layout: "horizontal" | "vertical"
  showDescription: "yes" | "no"
  buyButtonLabel: string
  /**
   * Resolved by the storefront renderer through the registered resolvers
   * (resolveData below). The editor shows a placeholder.
   */
  _product?: SiteProduct | null
}

export const ProductCard: ComponentConfig<ProductCardProps> = {
  fields: {
    productHandle: { type: "text", label: "Product handle", contentEditable: true },
    layout: {
      type: "radio",
      options: [
        { value: "vertical", label: "Vertical" },
        { value: "horizontal", label: "Horizontal" },
      ],
    },
    showDescription: {
      type: "select",
      options: [
        { label: "Show", value: "yes" },
        { label: "Hide", value: "no" },
      ],
    },
    buyButtonLabel: { type: "text", label: "Button label", contentEditable: true },
    background: {
      type: "radio",
      options: [
        { value: "none", label: "Transparent" },
        { value: "muted", label: "Muted" },
        { value: "accent", label: "Dark" },
      ],
    },
    paddingY: {
      type: "radio",
      options: [
        { value: "none", label: "None" },
        { value: "sm", label: "Small" },
        { value: "md", label: "Medium" },
        { value: "lg", label: "Large" },
      ],
    },
  },

  resolveData: async ({ props }) => {
    const resolvers = getResolvers()
    if (!resolvers || !props.productHandle) {
      return { props: {} }
    }
    const product = await resolvers.productByHandle(props.productHandle)
    return { props: { _product: product } }
  },

  defaultProps: {
    productHandle: "",
    layout: "horizontal",
    showDescription: "yes",
    buyButtonLabel: "Add to cart",
    background: "none",
    paddingY: "md",
  },

  render: ({
    layout,
    showDescription,
    buyButtonLabel,
    background,
    paddingY,
    _product,
  }) => {
    const s = sectionStyle({ background, paddingY })

    if (_product) {
      return (
        <section style={s}>
          <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px" }}>
            <ProductTile
              product={_product}
              ctaLabel={buyButtonLabel}
              layout={layout}
              showDescription={showDescription === "yes"}
            />
          </div>
        </section>
      )
    }

    const image = (
      <div
        style={{
          ...(layout === "horizontal" ? { width: 220, flexShrink: 0 } : { width: "100%" }),
          aspectRatio: "1/1",
          background: "#f3f4f6",
          borderRadius: 12,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#9ca3af",
          fontSize: 13,
        }}
      >
        Product
      </div>
    )

    return (
      <section style={s}>
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "0 24px" }}>
          <div
            style={{
              display: "flex",
              flexDirection: layout === "horizontal" ? "row" : "column",
              gap: 20,
              border: "1px solid #e5e7eb",
              borderRadius: 16,
              padding: 20,
              background: "#fff",
              alignItems: layout === "horizontal" ? "center" : "stretch",
            }}
          >
            {image}
            <div style={{ display: "flex", flexDirection: "column", gap: 8, flex: 1 }}>
              <div style={{ height: 18, width: "60%", background: "#e5e7eb", borderRadius: 4 }} />
              <div style={{ fontSize: "1.25rem", fontWeight: 800 }}>—</div>
              {showDescription === "yes" && <div style={{ height: 14, width: "90%", background: "#f3f4f6", borderRadius: 4 }} />}
              <div style={{ marginTop: "auto", paddingTop: 12 }}>
                <button
                  style={{
                    padding: "10px 20px",
                    background: "var(--site-accent, #7c3aed)",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  {buyButtonLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>
    )
  },
}
